import { Fragment } from "react";
import { Dialog, Transition } from "@headlessui/react";
import classNames from "classnames";
import FloatingButton from "./FloatingButton";
import { ChevronLeftIcon, ChevronRightIcon, CloseIcon } from "./fancyIcons";

const Lightbox = ({
  isVisible,
  src,
  alt,
  close,
  goToPreviousImage,
  goToNextImage,
}: {
  isVisible: boolean;
  src: string;
  alt: string;
  close: () => void;
  goToPreviousImage?: () => void;
  goToNextImage?: () => void;
}) => {
  const fullScreenDialogContainerClasses = classNames(
    "tw-fixed",
    "tw-inset-0",
    "tw-z-50",
    "tw-overflow-y-auto",
    "tw-flex",
    "tw-items-center",
    "tw-justify-center",
    "tw-font-display",
  );

  const backdropClasses = classNames(
    "tw-backdrop-blur-sm",
    "tw-fixed tw-inset-0",
    "tw-bg-neutral-900/90",
  );

  const containerClasses = classNames(
    "tw-max-h-[100dvh] tw-max-h-[100vh]",
    "tw-max-w-full",
  );

  return (
    <Transition show={isVisible} as={Fragment}>
      <Dialog
        onClose={close}
        as="div"
        className={fullScreenDialogContainerClasses}
      >
        {/* First transition child applies to backdrop */}
        <Transition.Child
          as={Fragment}
          enter="tw-ease-out tw-duration-200"
          enterFrom="tw-opacity-0"
          enterTo="tw-opacity-100"
          leave="tw-ease-in tw-duration-100"
          leaveFrom="tw-opacity-100"
          leaveTo="tw-opacity-0"
        >
          <div className={backdropClasses}></div>
        </Transition.Child>
        <Transition.Child
          as={Fragment}
          enter="tw-ease-out tw-duration-200"
          enterFrom="tw-opacity-0"
          enterTo="tw-opacity-100"
          leave="tw-ease-in tw-duration-100"
          leaveFrom="tw-opacity-100"
          leaveTo="tw-opacity-0"
        >
          <div>
            <FloatingButton
              type="default"
              onClick={close}
              className="tw-fixed tw-bottom-auto tw-left-4 tw-top-4 tw-z-[60] tw-flex tw-items-center"
            >
              <CloseIcon className="tw-fill-neutral-500 tw-stroke-neutral-500" />{" "}
              Close
            </FloatingButton>
          </div>
        </Transition.Child>
        <Transition.Child
          as={Fragment}
          enter="tw-ease-out tw-delay-100 tw-duration-200"
          enterFrom="tw-opacity-0 tw-scale-95"
          enterTo="tw-opacity-100 tw-scale-100"
          leave="tw-ease-in tw-duration-100"
          leaveFrom="tw-opacity-100 tw-scale-100"
          leaveTo="tw-opacity-0 tw-scale-95"
        >
          <Dialog.Panel as="div" className="tw-z-50 tw-w-full">
            <div
              className={classNames(
                "tw-flex tw-flex-row tw-items-center tw-p-6",
                "tw-z-50",
                {
                  "tw-justify-between": goToPreviousImage || goToNextImage,
                  "tw-justify-center": !goToPreviousImage && !goToNextImage,
                },
              )}
              // close lightbox when clicking on anything in this container except
              // for the prev and next image chevrons
              onClick={close}
            >
              {goToPreviousImage && (
                <ChevronLeftIcon
                  className="tw-size-20 tw-cursor-pointer tw-fill-neutral-500 tw-stroke-neutral-500"
                  onClick={(e) => {
                    e.stopPropagation(); // prevents closing lightbox when clicking on chevron
                    goToPreviousImage();
                  }}
                />
              )}
              <img
                src={src}
                alt={alt}
                className={classNames(containerClasses, "tw-object-contain")}
              />
              {goToNextImage && (
                <ChevronRightIcon
                  className="tw-size-20 tw-cursor-pointer tw-fill-neutral-500 tw-stroke-neutral-500"
                  onClick={(e) => {
                    e.stopPropagation(); // prevents closing lightbox when clicking on chevron
                    goToNextImage();
                  }}
                />
              )}
            </div>
          </Dialog.Panel>
        </Transition.Child>
      </Dialog>
    </Transition>
  );
};

export default Lightbox;
